import { IProjectInfo } from 'library/ts/IProjectInfo';
import { ProjectInfo } from 'library/ts/ProjectInfo';
import { objectDecode } from 'library/ts/utilities/modules/Convert';
import { hasSubstring } from 'library/ts/utilities/modules/String';
import { isBase64 } from 'library/ts/utilities/modules/Type';
import { getQueryField } from 'library/ts/utilities/modules/URL';

/**
 * @copyright © Paramount Streaming
 * @author Stan Gletski
 * @description Resolves whether the Nerd Mode plugin should be preloaded — computed once,
 * synchronously, at `DefaultTagConfig.ts` module-evaluation time (well before any
 * `SmartTag.create()` call), so it can feed the one flag the standard Tag-level plugin
 * mechanism already reads for both "fetch the script" and "construct once fetched"
 * (`TagConfigNerdMode.enabled` / `params.import.app.enabled` — see `TagConfigNerdMode.ts`).
 *
 * Two independent trigger channels, mirroring the split between Web and CTV:
 * - **Web** — any of `?nerdmode=`, `?nerd=`, or `?nerdcapture=` present on the page's own
 *   URL, **regardless of value** (including `nerdmode=0`/`verbose`/anything else) — this
 *   gate only answers "should the plugin load at all," never "should it auto-show." That
 *   distinction (show vs. verbose vs. capture-only vs. explicitly-suppressed) is entirely
 *   `NerdModeStartupResolver`'s job, unchanged, once the plugin has actually loaded.
 *   `nerdcapture` must be included here even though it never opens the overlay — it's the
 *   documented "silent background capture, no UI" trigger (e.g. `?nerdcapture=1` with no
 *   `nerdmode`/`nerd` present at all); without it, capture-only URLs would silently never
 *   fetch the plugin in the first place.
 * - **CTV (+ optionally Web)** — the same idea, base64-encoded, on the Tag script's own
 *   `<script src>` — CTV apps construct that URL directly but generally can't set the
 *   top-level page URL, the same reason the existing `onReady` mechanism reads its payload
 *   off the script's own src rather than the page's.
 *
 * `findTagScriptUrl()` replicates the minimal part of `RuntimeVO.appURL()`'s script-tag
 * scan needed here, without depending on the full `RuntimeVO`/`Utilities` bootstrap (cookie
 * and debug-cookie machinery irrelevant to this one check) — safe to call from a plain
 * config module with no framework context.
 *
 * A host can still override the resulting default either way via a normal TagConfig merge
 * — this only decides the *default*.
 */

/** Mirrors `NerdModeQueryParam.NERDMODE`/`.NERD`/`.CAPTURE` — duplicated here since that enum
 * now lives inside the dynamically-loaded plugin and can no longer be statically imported by
 * the main bundle; the load-gate must exist before the code it gates. Deliberately excludes
 * `verbose`/`nerdexpand`/`nerddetached`/`nerdcapmax` — those are modifiers with no independent
 * meaning unless one of these three is also present (nothing to modify otherwise). */
const NERD_MODE_QUERY_PARAMS = [ 'nerdmode', 'nerd', 'nerdcapture' ];

const findTagScriptUrl = (): string | undefined => {
	const projectInfo: IProjectInfo = ProjectInfo;
	const fileName = projectInfo?.app_output_file?.tag?.js?.min?.value;
	if ( !fileName || typeof document === 'undefined' ) {
		return undefined;
	}

	const scripts = document.getElementsByTagName( 'script' );
	for ( let i = 0; i < scripts.length; i++ ) {
		if ( hasSubstring( scripts[ i ].src, [ fileName ] ) ) {
			return scripts[ i ].src;
		}
	}
	return undefined;
};

const readTrigger = ( url: string | undefined ): string | undefined => {
	if ( !url ) {
		return undefined;
	}
	for ( const param of NERD_MODE_QUERY_PARAMS ) {
		const value = getQueryField( param, url );
		if ( value ) {
			return value;
		}
	}
	return undefined;
};

/**
 * @description Resolves the Nerd Mode plugin's bootstrap-enabled default. See file-level
 * description above for the two trigger channels.
 * @returns True if either trigger fired for this page/script — the plugin's default `enabled` value
 */
export const resolveNerdModeBootstrapEnabled = (): boolean => {
	// Web: any matching param key being present at all is enough — presence, not value,
	// decides "load or not." A bare `?nerdmode=0`/`?nerdcapture=0` still loads the plugin
	// (NerdModeStartupResolver alone decides whether that then means "explicitly suppress
	// auto-show" once it runs) — this gate never inspects what the value actually says.
	const webTrigger = readTrigger( typeof window !== 'undefined' ? window.location.href : undefined );
	if ( webTrigger !== undefined ) {
		return true;
	}

	// CTV (+ optional Web): base64 payload on the script's own src — a real config object,
	// not a simple flag, so a truthy-decode check (rather than presence alone) is correct here.
	const scriptTrigger = readTrigger( findTagScriptUrl() );
	if ( scriptTrigger === undefined ) {
		return false;
	}

	if ( isBase64( scriptTrigger ) ) {
		try {
			return !!objectDecode( scriptTrigger );
		} catch {
			return false;
		}
	}

	return true;
};